import React from 'react';
import {
  Briefcase, Calendar, Clock, ChevronRight, CheckCircle2, Circle, RefreshCw,
} from 'lucide-react';

/**
 * JobCard
 *
 * Shared presentational card for a single job task (type === 'job'). Rendered
 * by both the Customer Profile's `JobsTab` (full mixed history) and the new
 * `OneTimeJobsSection` (Overview at-a-glance, non-recurring only).
 *
 * Pure UI — no Firestore reads, no modal ownership, no side-effects. The
 * caller passes a click handler that typically opens a `WorkOrderSheet`.
 *
 * Why this lives here and not in /components/ui:
 *   - The status vocabulary (Ready to Schedule / Scheduled / In Progress /
 *     Completed / Cancelled) is the CSR's lens on a job. Dispatch uses a
 *     different vocabulary (En Route / On Site / Complete) because its
 *     concern is technician execution, not customer-profile readability.
 *     Merging the two would be a false abstraction.
 *   - The date/time formatters are scoped to a customer-profile reading
 *     context (full weekday + month + day + year). Calendar grid views use
 *     different formatters by design (hour-of-day labels, time ranges).
 */

const STATUS_CONFIG = {
  approved:    { icon: Circle,       bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', label: 'Ready to Schedule', iconColor: 'text-orange-400' },
  pending:     { icon: Circle,       bg: 'bg-blue-50',   border: 'border-blue-200',  badge: 'bg-blue-100 text-blue-700',     label: 'Scheduled',         iconColor: 'text-blue-400'   },
  scheduled:   { icon: Circle,       bg: 'bg-blue-50',   border: 'border-blue-200',  badge: 'bg-blue-100 text-blue-700',     label: 'Scheduled',         iconColor: 'text-blue-400'   },
  en_route:    { icon: Circle,       bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700',   label: 'En Route',          iconColor: 'text-amber-500'  },
  on_site:     { icon: Circle,       bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700',   label: 'On Site',           iconColor: 'text-amber-500'  },
  in_progress: { icon: Circle,       bg: 'bg-amber-50',  border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700',   label: 'In Progress',       iconColor: 'text-amber-500'  },
  completed:   { icon: CheckCircle2, bg: 'bg-green-50',  border: 'border-green-200', badge: 'bg-green-100 text-green-700',   label: 'Completed',         iconColor: 'text-green-500'  },
  cancelled:   { icon: Circle,       bg: 'bg-red-50',    border: 'border-red-200',   badge: 'bg-red-100 text-red-700',       label: 'Cancelled',         iconColor: 'text-red-400'    },
};

const formatScheduledDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return null; }
};

const formatScheduledTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return null; }
};

const JobCard = ({ task, onClick }) => {
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;
  const checkedCount = (task.checklist || []).filter((i) => i.checked).length;
  const totalCount = (task.checklist || []).length;
  const photoCount = (task.photos || []).length;
  const isRecurring = task.isRecurring === true;

  return (
    <button
      type="button"
      onClick={() => onClick?.(task)}
      className={`w-full text-left border rounded-xl p-4 transition-all hover:shadow-md active:scale-[0.99] ${cfg.bg} ${cfg.border}`}
      data-testid={`job-card-${task.id}`}
    >
      <div className="flex items-start gap-3">
        <StatusIcon className={`w-5 h-5 mt-0.5 shrink-0 ${cfg.iconColor}`} />

        <div className="flex-1 min-w-0">
          {/* Title + status badge + recurring marker */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {task.title || 'Job'}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badge}`}>
              {cfg.label}
            </span>
            {isRecurring && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700"
                data-testid={`job-card-${task.id}-recurring-pill`}
                title="Part of a recurring program"
              >
                <RefreshCw className="w-3 h-3" />
                Recurring
                {task.visitNumber ? ` #${task.visitNumber}` : ''}
              </span>
            )}
          </div>

          {/* Scheduled date + time */}
          {task.scheduledStart && (
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                {formatScheduledDate(task.scheduledStart)}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="w-3.5 h-3.5" />
                {formatScheduledTime(task.scheduledStart)}
              </span>
            </div>
          )}

          {!task.scheduledStart && task.status === 'approved' && (
            <div className="mt-1.5 text-xs text-orange-600 italic">
              Needs scheduling
            </div>
          )}

          {/* Progress indicators */}
          <div className="flex items-center gap-3 mt-2">
            {totalCount > 0 && (
              <span className="text-xs text-gray-400">
                ✓ {checkedCount}/{totalCount} checklist
              </span>
            )}
            {photoCount > 0 && (
              <span className="text-xs text-gray-400">
                {photoCount} photo{photoCount !== 1 ? 's' : ''}
              </span>
            )}
            {task.completedAt && (
              <span className="text-xs text-green-600">
                Completed {new Date(task.completedAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
      </div>
    </button>
  );
};

export default JobCard;

// Empty-icon export available to consumers building empty states alongside JobCard.
export const EmptyJobsIcon = Briefcase;
